import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Text, Button, Spinner, useBreakpointValue } from '@chakra-ui/react';
import axiosInstance from './../services/axiosInstance';
import useVideoTracking from './../../hooks/useVideoTracking';

const VideoPlayer = ({ idvdocipher, videoId, video }) => {
  const [otp, setOtp] = useState('');
  const playerRef = useRef(null);
  const [playbackInfo, setPlaybackInfo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isUrlResponsive, setIsUrlResponsive] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    const validateUrlAndFetchVdo = async () => {
      setLoading(true);
      setError('');
      let responsive = false;

      // 1. Check if URL exists and is responsive (ONLY on PC/Desktop)
      if (video?.url && !isMobile) {
        try {
          const baseUrl = process.env.REACT_APP_API_URL || 'https://api.cobra-education.com';
          const videoUrl = video.url;
          const fullVideoUrl = `${baseUrl}${videoUrl.startsWith('/') ? '' : '/'}${videoUrl}`;

          // Use a simple fetch to check if URL is reachable
          const response = await fetch(fullVideoUrl, { method: 'GET' });
          if (response.ok) {
            responsive = true;
          }
        } catch (e) {
          console.warn('Video URL is not responsive:', e);
          responsive = false;
        }
      }

      setIsUrlResponsive(responsive);

      // 2. If not responsive or no URL, fetch VdoCipher info
      if (!responsive && idvdocipher) {
        try {
          const response = await axiosInstance.get(`/vdocipher/${idvdocipher}`);
          const { otp, playbackInfo } = response.data;
          setOtp(otp);
          setPlaybackInfo(playbackInfo);
        } catch (err) {
          console.error('خطأ في جلب OTP ومعلومات التشغيل:', err);
          setError("فشل تحميل الفيديو. الرجاء المحاولة مرة أخرى لاحقًا.");
        }
      }

      setLoading(false);
    };

    validateUrlAndFetchVdo();
  }, [idvdocipher, video?.url]);

  const handleOpenApp = () => {
    const baseUrl = process.env.REACT_APP_API_URL || 'https://api.cobra-education.com';
    const videoUrl = video?.url || '';
    const fullVideoUrl = `${baseUrl}${videoUrl.startsWith('/') ? '' : '/'}${videoUrl}`;
    const deepLink = `cobra-edu://open?url=${fullVideoUrl}`;
    window.location.href = deepLink;
  };

  const handleDownloadApp = () => {
    window.open('https://api.cobra-education.com/uploads/apps/Cobra-Player-Setup.exe', '_blank');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="360px" dir="rtl">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error && !isUrlResponsive) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="360px" dir="rtl">
        <Text color="red.500">{error}</Text>
      </Box>
    );
  }

  // If video URL is responsive, show the app redirect placeholder
  if (isUrlResponsive) {
    return (
      <Box
        width="100%"
        height="100%"
        mx="auto"
        dir="rtl"
        bg="gray.900"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        color="white"
        p={6}
        textAlign="center"
      >
        <Box mb={6}>
          <Text fontSize="2xl" fontWeight="bold" mb={2}>مشاهدة الفيديو</Text>
          <Text color="gray.400" mb={6}>للحصول على أفضل تجربة وحماية المحتوى، يرجى استخدام تطبيق COBRA PLayer</Text>
        </Box>

        <Box display="flex" flexDirection="column" gap={4} width="100%" maxW="300px">
          <Button
            colorScheme="blue"
            size="lg"
            onClick={handleOpenApp}
            leftIcon={<span>🚀</span>}
          >
            فتح في التطبيق
          </Button>

          <Button
            variant="outline"
            colorScheme="whiteAlpha"
            size="md"
            onClick={handleDownloadApp}
            leftIcon={<span>📥</span>}
          >
            تحميل تطبيق الكمبيوتر (PC)
          </Button>
        </Box>

        <Text mt={8} fontSize="xs" color="gray.500">
          إذا قمت بتثبيت التطبيق بالفعل، سيتم محاولة فتحه تلقائياً.
        </Text>
      </Box>
    );
  }

  // Fallback to VdoCipher if URL is not responsive or missing
  return (
    <Box width="100%" height={450} mx="auto" dir="rtl">
      {otp && playbackInfo && (
        <iframe
          border={"2px black solid"}
          width="100%"
          height="100%"
          src={`https://player.vdocipher.com/v2/?otp=${otp}&playbackInfo=${playbackInfo}`}
          allow="encrypted-media"
          allowFullScreen
          frameBorder="0"
          ref={playerRef}
        ></iframe>
      )}
    </Box>
  );
};

VideoPlayer.propTypes = {
  video: PropTypes.object,
  idvdocipher: PropTypes.string,
  videoId: PropTypes.string
};

export default VideoPlayer;


